import Vue from 'vue'
import App from '@/App.vue'
import { router } from '@/router'
import store from '@/store'
import Axios from 'axios'
import VueLogger from 'vuejs-logger'
import GetTextPlugin from 'vue-gettext'
import translations from '@/locales/fr_FR/translations.json'
import VeeValidate, { Validator } from 'vee-validate'
import FlagIcon from 'vue-flag-icon'
import PrettyCheckbox from 'pretty-checkbox-vue'
import BootstrapVue from 'bootstrap-vue'
import { config } from '@/config.settings'
import 'bootstrap'
import LiquorTree from 'liquor-tree'
import { fields_en, fields_fr } from '@/plugins/fields_tr'
import VueToastify from 'vue-toastify'
import VueWaypoint from 'vue-waypoint'
import '@/plugins/validators'
import VueClipboard from 'vue-clipboard2'
import 'overlayscrollbars/css/OverlayScrollbars.css'
import { OverlayScrollbarsPlugin } from 'overlayscrollbars-vue'
import apiMethod from '@/api_call'
import VueMoment from 'vue-moment'
import moment from 'moment'
import { datetimePicker } from '@vendors/bootstrap-datetimepicker/bootstrap-datetimepicker'
import 'moment/dist/locale/fr'
import jQuery from 'jquery'
import '@/scss/new-design-system/index.scss'
import { FALLBACK_LOCALE } from '@/constants/Language.constants'
import { getNavigatorLocale, localeToLang } from '@/helpers/Language.helpers'
import { i18n, setI18nLocale } from '@/I18n.ts'
import { useErrorLogging } from '@/composables/UseErrorLogging.composables.ts'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { datadogRum } from '@datadog/browser-rum'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

/*global OverlayScrollbars*/
if (config.datadog_enabled) {
  Vue.prototype.$datadogRum = datadogRum
  datadogRum.init({
    applicationId: config.datadog_application_id,
    clientToken: config.datadog_client_token,
    site: config.datadog_site,

    service: config.datadog_service,
    env: config.datadog_env,
    allowedTracingUrls: [/^https?:\/\/(?:[a-z0-9-]+\.)*tenacy\.io(?:\/.*)?$/],
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackBfcacheViews: true,
    defaultPrivacyLevel: 'mask-user-input',
    trackResources: false,
    trackLongTasks: false,
    trackUserInteractions: true,
  })
}

Vue.config.productionTip = false
Vue.config.errorHandler = (err, vm, info) => console.error(err, vm, info)

const { logError } = useErrorLogging()

Vue.prototype.$language = {
  current: getNavigatorLocale().replace('-', '_'),
}

Vue.prototype.getCurrentLang = () => localeToLang(Vue.prototype.$language.current)

Vue.prototype._ = function (text) {
  // TODO: delete or refact when introducing i18n
  return Vue.prototype.$gettext(text, Vue.prototype.$language.current)
}

setI18nLocale(Vue.prototype.$language.current)

window.$ = jQuery
window.jQuery = jQuery

window.moment = moment
Vue.use(VueMoment, { moment })
datetimePicker(jQuery, moment)

Vue.use(VueWaypoint)
Vue.use(LiquorTree)
Vue.use(BootstrapVue)
Vue.use(PrettyCheckbox)
Vue.use(OverlayScrollbarsPlugin)

// Your custom options
Vue.prototype.$http = Axios
Vue.prototype.$httpController = new AbortController()
Vue.prototype.$http.defaults.withCredentials = true

Vue.use(FlagIcon)
Vue.use(VueLogger, {
  logLevel: config.log_level ? config.log_level : 'error',
  stringifyArguments: false,
  showLogLevel: true,
  showMethodName: true,
  separator: '|',
  showConsoleColors: true,
})

Vue.use(GetTextPlugin, {
  availableLanguages: {
    en_US: 'English',
    fr_FR: 'Français',
  },
  defaultLanguage: FALLBACK_LOCALE,
  languageVmMixin: {
    computed: {
      currentKebabCase: function () {
        return this.current.toLowerCase().replace('_', '-')
      },
    },
  },
  translations: translations,
  silent: true,
})

Vue.use(VeeValidate, {
  classes: true,
  events: 'blur',
  classNames: {
    valid: 'is-valid',
    invalid: 'is-invalid',
  },
  dictionary: {
    fr: {
      attributes: fields_fr,
    },
    en: {
      attributes: fields_en,
    },
  },
})

Vue.prototype.confirm = function (
  message,
  ok = null,
  ko = null,
  force = false,
  okVariant = 'outline-primary',
  title = '',
  hideHeaderClose = true,
  headerClass = null
) {
  if (force) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    this.$bvModal
      .msgBoxConfirm(message ?? this._('Are you sure?'), {
        noCloseOnEsc: true,
        noCloseOnBackdrop: true,
        okTitle: ok ?? this._('Yes'),
        cancelTitle: ko ?? this._('No'),
        cancelVariant: 'light',
        okVariant,
        title,
        hideHeaderClose,
        headerClass,
      })
      .then((ok) => {
        if (ok) {
          resolve()
        } else {
          reject(ok)
        }
      })
      .catch((err) => {
        reject(err)
      })
  })
}

Vue.prototype.msg_confirm = function (message, ok = null, force = false) {
  if (typeof message == 'undefined') {
    return
  }
  if (force) {
    return new Promise((resolve) => resolve())
  }
  if (ok === null) {
    ok = this._('Yes')
  }
  return new Promise((resolve, reject) => {
    this.$bvModal
      .msgBoxOk(message, {
        noCloseOnEsc: true,
        noCloseOnBackdrop: true,
        okTitle: ok,
        cancelVariant: 'light',
        okVariant: 'outline-primary',
      })
      .then((ok) => {
        if (ok) {
          resolve()
        } else {
          reject()
        }
      })
      .catch((err) => {
        reject(err)
      })
  })
}

Vue.use(VueToastify, {
  theme: 'light',
  position: 'bottom-center',
  duration: 5000,
  errorDuration: 6000,
  successDuration: 2000,
  warningInfoDuration: 3000,
  customNotifications: {
    neutral: {
      type: 'neutral',
    },
  },
})

Vue.use(VueClipboard)

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
Vue.use(pinia)

const dict = {
  custom: {
    link: {
      url: Vue.prototype._('Invalid URL'),
    },
  },
}
Validator.localize('en', dict)

Vue.prototype.$toast = {
  alert: (msg) => {
    Vue.prototype.$vToastify.error(msg, Vue.prototype._('Error') || 'Error')
  },
  warning: (msg) => {
    Vue.prototype.$vToastify.warning(msg, Vue.prototype._('Warning') || 'Warning')
  },
  warn: (msg) => {
    Vue.prototype.$vToastify.warning(msg, Vue.prototype._('Warning') || 'Warning')
  },
  info: (msg) => {
    Vue.prototype.$vToastify.info(msg, Vue.prototype._('Info') || 'Info')
  },
  success: (msg) => {
    Vue.prototype.$vToastify.success(msg, Vue.prototype._('Success') || 'Success')
  },
}

Vue.directive('click-outside', {
  bind: function (el, binding) {
    // Provided expression must evaluate to a function.
    if (typeof binding.value !== 'function') {
      //
    }
    // Define Handler and cache it on the element
    const bubble = binding.modifiers.bubble
    const handler = (e) => {
      if (bubble || (!el.contains(e.target) && el !== e.target)) {
        binding.value(e)
      }
    }
    el.__vueClickOutside__ = handler

    // add Event Listeners
    document.addEventListener('click', handler)
  },

  unbind: function (el) {
    // Remove Event Listeners
    document.removeEventListener('click', el.__vueClickOutside__)
    el.__vueClickOutside__ = null
  },
})

Vue.prototype.apiError = function (error, set_unloading = true, msg = null, warn = false) {
  this.$log.error(error)
  if (set_unloading) {
    store.commit('unloading')
  }
  if (error && error.response && error.response.status === 403) {
    this.$toast.alert(this._('Your session has expired.'))
    return
  }
  if (msg) {
    if (warn) {
      this.$toast.warn(msg)
    } else {
      this.$toast.alert(msg)
    }
  } else {
    this.$toast.alert(this._('Data cannot be loaded at the moment.'))
  }
}

Vue.prototype.$api = apiMethod

/**
 * Cancel ongoing requests by creating a new AbortController instance
 * and assigning it to the Vue instance.
 */
Vue.prototype.cancelRequests = function () {
  Vue.prototype.$httpController.abort()
  Vue.prototype.$httpController = new AbortController()
}

const downloadFile = (url) => {
  Vue.prototype
    .$http({
      url: url,
      method: 'GET',
      responseType: 'blob', // important
    })
    .then((response) => {
      if (response.data && response.data.ok && response.data.ok === false) {
        throw new Error()
      }
      let filename = response.headers['content-disposition']
      if (!filename || filename === '') {
        filename = 'no_filename'
      } else {
        filename = filename.replace('attachment; filename=', '')
      }
      if (filename[0] === '"') {
        const from = 1
        filename = filename.substring(from, from + filename.length - 2)
      }
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
    })
    .catch((err) => {
      logError(err)
      Vue.prototype.$toast.alert(Vue.prototype._('You cannot download this file'))
      throw Error(err)
    })
}

Vue.prototype.download_file = downloadFile

void store.dispatch('init')

jQuery.extend(true, jQuery.fn.datetimepicker.defaults, {
  icons: {
    time: 'far fa-clock',
    date: 'far fa-calendar',
    up: 'far fa-arrow-up',
    down: 'far fa-arrow-down',
    right: 'far fa-arrow-right',
    previous: 'far fa-chevron-left',
    next: 'far fa-chevron-right',
    today: 'far fa-calendar-check',
    clear: 'far fa-trash-alt',
    close: 'far fa-times-circle',
  },
})

new Vue({
  i18n,
  router,
  store,
  render: (h) => h(App),
}).$mount('#app')
document.addEventListener('DOMContentLoaded', function () {
  //The first argument are the elements to which the plugin shall be initialized
  //The second argument has to be at least a empty object or a object with your desired options
  Vue.prototype.$os_body = OverlayScrollbars(document.querySelectorAll('body'), {
    scrollbars: { autoHide: 'scroll', autoHideDelay: 400 },
  })
  $('body').append($('.d3-tooltip'))
  $('body').append($('.tui-tooltip'))
})
